import {
  SchedulesClient,
  DayOfWeek,
  RecurringType,
  UpdateScheduleDto,
} from "../../services";
import { GetNewInstance, Store } from "../../helpers";
import { useRecoilState } from "recoil";
import {
  ModalTypeState,
  ScheduleTimelineReloadState,
  SelectedScheduleState,
} from "../../atoms";
import { useEffect, useRef, useState } from "react";
import {
  Chip,
  CustomDatePicker,
  CustomDateTimePicker,
  CustomTimePicker,
} from "../Misc";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
import { RepeatSelectionDropDown } from "../actionButtons";
import { SpinnerButton } from "../buttons";
import { toast } from "react-toastify";

type Props = {
  onSuccess: () => void;
  onFailure?: () => void;
};

export function UpdateScheduleForm({ onSuccess, onFailure }: Props) {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const [isRepeat, setIsRepeat] = useState<boolean>(false);
  const [selectedRepeat, setSelectedRepeat] = useState<RecurringType>(
    RecurringType.None
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState<Date | undefined>(undefined);
  const [endRepeateDate, setEndRepeateDate] = useState<Date | undefined>(
    undefined
  );
  const [scheduleDuration, setScheduleDuration] = useState<Date | undefined>(
    undefined
  );
  const [maxDuration, setMaxDuration] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<dayjs.Dayjs>();
  const [, setModalType] = useRecoilState(ModalTypeState);
  const [timlineReload, setTimlineReload] = useRecoilState(
    ScheduleTimelineReloadState
  );
  const [selectedWeekDays, setSelectedWeekDays] = useState<number[]>([]);
  const [selectedSchedule] = useRecoilState(SelectedScheduleState);
  const [showSpinner, setShowSpinner] = useState(false);

  const usersCustomerId = Store.getCustomerId();
  const currentCustomer = Store.getCurrentCustomer();
  const client = new SchedulesClient(undefined, GetNewInstance());
  const durationInSecounds = useRef<number>(0);
  dayjs.extend(duration);

  useEffect(() => {
    let startDay = dayjs(selectedSchedule?.startDate).tz(
      currentCustomer?.timeZoneIANA
    );
    let endDay = dayjs(selectedSchedule?.endDate).tz(
      currentCustomer?.timeZoneIANA
    );
    durationInSecounds.current = dayjs
      .duration(endDay.diff(startDay))
      .asSeconds();

    let newStartTime = new Date(
      0,
      0,
      0,
      startDay.hour(),
      startDay.minute(),
      startDay.second()
    );
    setStartTime(newStartTime);
    const newStartDate = new Date(
      startDay.year(),
      startDay.month(),
      startDay.date()
    );
    setMaxDuration(
      dayjs(new Date(0, 0, 0, 23, 59, 59))
        .subtract(newStartTime.getHours(), "hour")
        .subtract(newStartTime.getMinutes(), "minute")
        .subtract(newStartTime.getSeconds(), "second")
        .toDate()
    );
    setStartDate(newStartDate);

    const newDuration = new Date(0, 0, 0, 0, 0, durationInSecounds.current);
    setScheduleDuration(newDuration);
    setEndDate(endDay);
    let isRepeat = selectedSchedule?.recurringType !== RecurringType.None;
    setIsRepeat(isRepeat);
    setSelectedRepeat(selectedSchedule?.recurringType ?? RecurringType.Daily);
    if (
      selectedSchedule?.recurringType !== RecurringType.None &&
      selectedSchedule?.recurringEndDateTime
    ) {
      let recurringEndDate = dayjs(selectedSchedule?.recurringEndDateTime).tz(
        currentCustomer?.timeZoneIANA
      );
      setEndRepeateDate(
        new Date(
          recurringEndDate.year(),
          recurringEndDate.month(),
          recurringEndDate.date(),
          recurringEndDate.hour(),
          recurringEndDate.minute(),
          recurringEndDate.second()
        )
      );
    }
    const workDays = selectedSchedule?.recurringDays?.map((x) => {
      let index = Object.keys(DayOfWeek).indexOf(x);
      return index === 0 ? 6 : index - 1;
    });
    setSelectedWeekDays(workDays ?? []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnSubmit = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setIsRepeat(selectedSchedule?.recurringType !== RecurringType.None);
    if (
      startDate === undefined ||
      startTime === undefined ||
      duration === undefined
    ) {
      return;
    }

    let request = new UpdateScheduleDto();
    request.id = selectedSchedule?.id;
    request.canvasId = selectedSchedule?.canvasId;
    request.recurringStartDateTime = selectedSchedule?.recurringStartDateTime;

    let startDay = dayjs(
      new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        startTime.getHours(),
        startTime.getMinutes(),
        startTime.getSeconds()
      )
    ).tz(currentCustomer?.timeZoneIANA, true);

    request.startDate = startDay.toDate();
    request.endDate = endDate?.toDate();
    request.recurringType = isRepeat ? selectedRepeat : RecurringType.None;
    request.recurringDays = isRepeat
      ? selectedWeekDays.map((x) => convertSelectionToDayOfWeekEnum(x))
      : [];

    if (isRepeat && endRepeateDate) {
      let endRepeatDay = dayjs(
        new Date(
          endRepeateDate.getFullYear(),
          endRepeateDate.getMonth(),
          endRepeateDate.getDate(),
          endRepeateDate.getHours(),
          endRepeateDate.getMinutes(),
          endRepeateDate.getSeconds()
        )
      ).tz(currentCustomer?.timeZoneIANA, true);

      request.recurringEndDateTime = endRepeatDay.toDate();
    } else {
      request.recurringEndDateTime = undefined;
    }

    if (usersCustomerId) {
      setShowSpinner(true);
      client
        .updateSchedule(usersCustomerId, request)
        .then((response) => {
          toast.success("Schedule updated successfully");
          setModalType(null);
          setTimlineReload(!timlineReload);
          onSuccess();
        })
        .catch((error) => {
          setModalType(null);
        })
        .finally(() => setShowSpinner(false));
    }
  };

  const handleDurationChange = (updated: Date) => {
    setScheduleDuration(updated);
  };

  const handleStartTimeChange = (updated: Date) => {
    setStartTime(updated);
    setMaxDuration(
      dayjs(new Date(0, 0, 0, 23, 59, 59))
        .subtract(updated.getHours(), "hour")
        .subtract(updated.getMinutes(), "minute")
        .subtract(updated.getSeconds(), "second")
        .toDate()
    );
  };

  useEffect(() => {
    if (
      startDate === undefined ||
      startTime === undefined ||
      scheduleDuration === undefined
    ) {
      return;
    }

    let startDay = dayjs(
      new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        startTime.getHours(),
        startTime.getMinutes(),
        startTime.getSeconds()
      )
    ).tz(currentCustomer?.timeZoneIANA, true);

    let durationDay = dayjs(scheduleDuration);

    let endDay = startDay.add(durationDay.hour(), "hour");
    endDay = endDay.add(durationDay.minute(), "minute");
    endDay = endDay.add(durationDay.second(), "second");
    setEndDate(endDay);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, startDate, scheduleDuration]);

  const convertSelectionToDayOfWeekEnum = (num: number): DayOfWeek => {
    const index = num + 1;
    return Object.values(DayOfWeek)[index === 7 ? 0 : index];
  };

  return (
    <form className="py-4 text-sm text-white">
      <div className="my-4">
        <label className="flex flex-col">
          <span>Canvases</span>
          <div className="h-auto flex-wrap rounded-md border border-page-border my-4 bg-Secondary-Input-bg">
            <div className="flex-wrap flex p-1 gap-1 items-center">
              <Chip className="bg-Secondary-Dashboard-bg text-gray-100 px-3 py-1 border-page-border ">
                {selectedSchedule?.canvaseName ?? ""}
              </Chip>

              <input
                disabled
                className="h-full bg-transparent border-0 px-2"
                type="text"
              />
            </div>
          </div>
          <small className="text-red-600"></small>
        </label>
      </div>
      <div className="flex flex-row gap-4 justify-between">
        <div className="my-4 w-full">
          <label className="flex flex-col">
            <span>Start date</span>
            {startDate !== undefined && (
              <CustomDatePicker
                value={startDate}
                onChange={(updated) => setStartDate(updated)}
              />
            )}
            <small className="text-red-600"></small>
          </label>
        </div>
        <div className="my-4 w-full">
          <label className="flex flex-col">
            <span>Start time</span>
            {startTime !== undefined && (
              <CustomTimePicker
                key="start_time"
                nameId="start"
                show24={false}
                value={startTime}
                onChange={(updated) => handleStartTimeChange(updated)}
              />
            )}
            <small className="text-red-600"></small>
          </label>
        </div>
      </div>
      <div className="flex flex-row gap-4 justify-between">
        <div className="my-4 w-full">
          <label className="flex flex-col">
            <span>Duration</span>
            {startTime !== undefined && (
              <CustomTimePicker
                key="duration"
                nameId="duration"
                show24
                value={scheduleDuration}
                maxTime={maxDuration}
                onChange={(updated) => handleDurationChange(updated)}
              />
            )}
            <small className="text-red-600"></small>
          </label>
        </div>
        <div className="my-4 w-full">
          <label className="flex flex-col">
            <span>End date</span>
            <div className="h-11 rounded-md border border-page-border flex justify-center items-center bg-input-bg overflow-hidden">
              <div className="h-full w-full flex justify-start text-gray-400 bg-transparent mx-4 items-center">
                {endDate && endDate.format("MM/DD/YYYY   hh:mm:ss A")}
              </div>
            </div>
            <small className="text-red-600"></small>
          </label>
        </div>
      </div>
      <div>
        <label className="flex items-center gap-2 px-1 py-2.5 w-36 cursor-pointer">
          <input
            className="border border-gray-400 focus:shadow-none bg-input-border w-4 h-4"
            type="checkbox"
            checked={isRepeat}
            onChange={(x) => setIsRepeat(!isRepeat)}
          />
          <span>Repeat schedule</span>
        </label>
      </div>
      {isRepeat ? (
        <div className={`flex flex-row gap-4 justify-between w-full `}>
          <div className="my-4 w-full">
            <label className="flex flex-col">
              <span>Repeat</span>
              <RepeatSelectionDropDown
                value={selectedRepeat}
                onChange={(value) => setSelectedRepeat(value)}
              />
              <small className="text-red-600"></small>
            </label>
          </div>
          <div className="my-4 w-full">
            <label className="flex flex-col">
              <span>End repeat</span>
              {
                <CustomDateTimePicker
                  value={endRepeateDate}
                  onChange={(updated) =>
                    setEndRepeateDate(updated ?? undefined)
                  }
                />
              }
              <small className="text-red-600"></small>
            </label>
          </div>
        </div>
      ) : (
        <></>
      )}
      {isRepeat && selectedRepeat === RecurringType.Weekly ? (
        <div className={`flex flex-row gap-4 justify-between w-full`}>
          <div className="my-4 w-full">
            <label className="flex flex-col">
              <span>Repeat schedule</span>
              <div className="flex flex-row gap-2">
                {["M", "T", "W", "T", "F", "S", "S"].map((x, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      selectedWeekDays.includes(i)
                        ? setSelectedWeekDays(
                            selectedWeekDays.filter((d) => d !== i)
                          )
                        : setSelectedWeekDays([...selectedWeekDays, i]);
                    }}
                    className={`rounded-full cursor-pointer h-8 w-8 flex justify-center items-center
                                        ${
                                          selectedWeekDays.includes(i)
                                            ? " bg-Palette-6"
                                            : "bg-Secondary-Input-bg"
                                        }                        
                                    `}
                  >
                    <span> {x}</span>
                  </div>
                ))}
              </div>
            </label>
          </div>
        </div>
      ) : (
        <></>
      )}

      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={() => {
            setModalType(null);
          }}
          className="py-2.5 px-4 rounded-md border border-page-border bg-white bg-opacity-5 hover:bg-opacity-10"
        >
          Cancel
        </button>
        <SpinnerButton
          className={`py-2.5 px-4 rounded-md bg-button-bg-primary-default hover:bg-button-bg-primary-hover active:shadow-button_primary ${
            showSpinner ? "disabled" : ""
          } `}
          showSpinner={showSpinner}
          onClick={handleOnSubmit}
        >
          Update schedule
        </SpinnerButton>
      </div>
    </form>
  );
}
